"use client";

import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2, Upload, FileIcon, Trash2, Download, X, FileText, Image, Film, Archive, File } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import FileService from '@/api/file.service';
import { formatDistanceToNow, format } from 'date-fns';
import { formatBytes } from '@/lib/utils';
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";

interface FilesTabProps {
  files: any[];
  entityType: 'customer' | 'deal';
  entityId: number;
  businessId?: number;
  isLoading: boolean;
  onFileAdded: (file: any) => void;
  onFileDeleted: (fileId: number) => void;
}

export function FilesTab({ files, entityType, entityId, businessId, isLoading, onFileAdded, onFileDeleted }: FilesTabProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (!businessId) return;
    
    setIsUploading(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append(entityType === 'customer' ? 'customerId' : 'dealId', entityId.toString());
        
        const response = await FileService.uploadFile(businessId, formData);
        
        if (response.data.result === 'success') {
          toast({
            title: "Success",
            description: `${file.name} uploaded successfully`,
          });
          onFileAdded(response.data.file);
        } else if (response.data.result === 'invalid') {
          window.location.href = '/logout';
          break;
        } else {
          toast({
            title: "Error",
            description: response.data.message || `Failed to upload ${file.name}`,
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: "Failed to upload file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    setIsDeleting(fileId);
    try {
      const response = await FileService.deleteFile(fileId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "File deleted successfully",
        });
        onFileDeleted(fileId);
      } else if (response.data.result === 'invalid') {
        window.location.href = '/logout';
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete file",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast({
        title: "Error",
        description: "Failed to delete file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleDownloadFile = async (fileId: number) => {
    setIsDownloading(fileId);
    try {
      const response = await FileService.downloadFile(fileId);
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'download';
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: "File download started",
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: "Error",
        description: "Failed to download file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(null);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="h-10 w-10 text-blue-500" />;
    } else if (fileType.startsWith('video/')) {
      return <Film className="h-10 w-10 text-purple-500" />;
    } else if (fileType.includes('pdf')) {
      return <FileText className="h-10 w-10 text-red-500" />;
    } else if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('tar') || fileType.includes('7z')) {
      return <Archive className="h-10 w-10 text-amber-500" />;
    } else {
      return <File className="h-10 w-10 text-slate-500" />;
    }
  };

  const isPreviewable = (fileType: string) => {
    return fileType.startsWith('image/') || fileType.includes('pdf');
  };

  const handleFilePreview = (file: any) => {
    setSelectedFile(file);
    setPreviewOpen(true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* File Upload Area */}
      <div 
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileSelection}
          multiple
        />
        
        <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" />
        
        <h3 className="text-base font-medium text-slate-700 dark:text-slate-300 mb-1">
          {isDragging ? 'Drop files here' : 'Drag & drop files here'}
        </h3>
        
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          or browse from your computer
        </p>
        
        <Button 
          onClick={() => fileInputRef.current?.click()} 
          disabled={isUploading}
          variant="outline"
          className="gap-2"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Select Files
            </>
          )}
        </Button>
      </div>

      {/* Files List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-slate-500">
            All Files
            {files.length > 0 && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                {files.length}
              </span>
            )}
          </h3>
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-md">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div>
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-24 mt-1" />
                  </div>
                </div>
                <Skeleton className="h-8 w-20 rounded" />
              </div>
            ))}
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-10 border border-dashed rounded-md bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
            <FileIcon className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500 dark:text-slate-400">No files uploaded yet</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Upload files to share with your team
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {files.map((file) => {
              const createdAt = new Date(file.createdAt);
              const timeAgo = formatDistanceToNow(createdAt, { addSuffix: true });
              
              return (
                <div key={file.id} className="flex items-center justify-between p-3 border rounded-md group hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors">
                  <div 
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => isPreviewable(file.fileType) && handleFilePreview(file)}
                  >
                    {getFileIcon(file.fileType)}
                    
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-primary transition-colors">
                        {file.fileName}
                      </p>
                      <div className="flex items-center text-xs text-slate-500 mt-1 gap-2">
                        <span>{formatBytes(file.fileSize)}</span>
                        <span>•</span>
                        <span>{timeAgo}</span>
                        {isPreviewable(file.fileType) && (
                          <>
                            <span>•</span>
                            <span className="text-primary">Click to preview</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDownloadFile(file.id)}
                      disabled={isDownloading === file.id}
                    >
                      {isDownloading === file.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      ) : (
                        <Download className="h-4 w-4 text-blue-500" />
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDeleteFile(file.id)}
                      disabled={isDeleting === file.id}
                    >
                      {isDeleting === file.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-red-500" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                {selectedFile?.fileName}
                <span className="text-xs text-slate-500 font-normal">
                  ({formatBytes(selectedFile?.fileSize)})
                </span>
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => setPreviewOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto mt-4">
            {selectedFile?.fileType.startsWith('image/') ? (
              <div className="flex items-center justify-center h-full">
                <img 
                  src={selectedFile.fileUrl || `${process.env.NEXT_PUBLIC_API_URL}/v1/download/file/${selectedFile.id}`} 
                  alt={selectedFile.fileName}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : selectedFile?.fileType.includes('pdf') ? (
              <iframe 
                src={selectedFile.fileUrl || `${process.env.NEXT_PUBLIC_API_URL}/v1/download/file/${selectedFile.id}`} 
                className="w-full h-full"
                title={selectedFile.fileName}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <FileIcon className="h-20 w-20 text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-700">Preview not available</p>
                <p className="text-sm text-slate-500 mt-1 mb-4">This file type cannot be previewed</p>
                <Button 
                  onClick={() => handleDownloadFile(selectedFile.id)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download File
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center mt-4 pt-4 border-t">
            <div className="text-xs text-slate-500">
              {selectedFile?.createdAt ? 
                `Uploaded ${format(new Date(selectedFile.createdAt), 'PPP')} by ${selectedFile?.user?.firstName || ''} ${selectedFile?.user?.lastName || ''}` :
                'Upload information unavailable'
              }
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => handleDownloadFile(selectedFile?.id)}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 